import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import AppPaths from '../../AppPaths';
import { collarProductIsSupportedInCountry, useLocale } from '../../contexts/LocaleContext';
import * as events from '../../lib/analytics/events';
import { useLaunchQuiz } from '../../lib/fiQuiz';
import { useIsGrowthBookEnabled } from '../../lib/growthbook';
import * as types from '../../types';
import { Image } from '../Image/Image';
import styles from './CrossShopSection.module.scss';

/**
 * "Still digging around? Find the Fi that fits." cross-shop module for the
 * bottom of the collar PDPs, so prospects can compare and jump between
 * Mini / Series 3+ / Ultra. Ported from the marketing site's CrossShopSection
 * (fitracking.com 2026 > Cross-Product Footer, node 7220:10533) — keep the two
 * in sync. Unlike the marketing site, learn-more and shop are the same page
 * here, so each card gets a single Shop CTA.
 */

export type CrossShopProductId = 'mini' | 'series3plus' | 'ultra';

type CrossShopProduct = {
  id: CrossShopProductId;
  collarId: types.CollarProductId;
  nameKey: string;
  taglineKey: string;
  shopCtaKey: string;
  shopPath: string;
  imageSrc: string;
  imageAltKey: string;
  /** Placement of the transparent product cutout inside the card. */
  imageClassName: string;
};

const miniProduct: CrossShopProduct = {
  id: 'mini',
  collarId: types.miniCollarId,
  nameKey: 'fi_mini',
  taglineKey: 'worlds_smallest_and_lightest_tracker',
  shopCtaKey: 'shop_mini',
  shopPath: AppPaths.Products('fi-mini'),
  imageSrc: '/images/cross-shop/mini.webp',
  imageAltKey: 'a_closeup_of_the_fi_mini',
  imageClassName: styles.cardImageMini,
};

const series3PlusProduct: CrossShopProduct = {
  id: 'series3plus',
  collarId: types.series3PlusCollarId,
  nameKey: 'fi_series_3_plus',
  taglineKey: 'up_to_3_months_battery_life',
  shopCtaKey: 'shop_series_3_plus',
  shopPath: AppPaths.Products(types.series3CollarId),
  imageSrc: '/images/cross-shop/series3plus.webp',
  imageAltKey: 'a_closeup_of_the_series_3_plus_collar',
  imageClassName: styles.cardImageSeries3Plus,
};

const ultraProduct: CrossShopProduct = {
  id: 'ultra',
  collarId: types.ultraCollarId,
  nameKey: 'ultra_fi_ultra',
  taglineKey: 'powered_by_t_satellite_with_starlink',
  shopCtaKey: 'shop_ultra',
  shopPath: AppPaths.Products('fi-ultra'),
  imageSrc: '/images/cross-shop/ultra.webp',
  imageAltKey: 'a_closeup_of_the_fi_ultra',
  imageClassName: styles.cardImageUltra,
};

function Chevron() {
  return (
    <svg width="8" height="14" viewBox="0 0 8 14" fill="none" aria-hidden="true">
      <path d="M1 1l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CrossShopCard({ product }: { product: CrossShopProduct }) {
  const { t } = useTranslation();
  const trackTapped = () => events.crossShopSection.productTapped(product.collarId);

  return (
    <div className={styles.cardColumn}>
      {/* The whole card is a link to the PDP (same destination as the Shop CTA below). */}
      <Link to={product.shopPath} aria-label={t(product.shopCtaKey)} className={styles.card} onClick={trackTapped}>
        <Image
          image={types.createImage({ url: product.imageSrc })}
          alt={t(product.imageAltKey)}
          className={product.imageClassName}
        />
        <div className={styles.cardText}>
          <h3 className={styles.cardTitle}>{t(product.nameKey)}</h3>
          <p className={styles.cardTagline}>{t(product.taglineKey)}</p>
        </div>
      </Link>
      <div className={styles.cardActions}>
        <Link to={product.shopPath} className={styles.yellowPill} onClick={trackTapped}>
          {t(product.shopCtaKey)}
        </Link>
      </div>
    </div>
  );
}

export default function CrossShopSection({ currentProduct }: { currentProduct: CrossShopProductId }) {
  const { t } = useTranslation();
  const launchQuiz = useLaunchQuiz();
  const { country } = useLocale();
  const internalTester = useSelector((state: types.AppState) => !!state.config.siteConfig.internalTester);
  const growthBookReady = useIsGrowthBookEnabled();

  const isMiniSupported = collarProductIsSupportedInCountry(types.miniCollarId, country);
  const isUltraSupported =
    growthBookReady && collarProductIsSupportedInCountry(types.ultraCollarId, country, internalTester);

  // Cross-shop only: hide the product whose page we're on, and never surface
  // products that aren't available in the shopper's country (e.g. Ultra
  // outside the US).
  const products = [
    ...(isMiniSupported ? [miniProduct] : []),
    series3PlusProduct,
    ...(isUltraSupported ? [ultraProduct] : []),
  ].filter((product) => product.id !== currentProduct);

  if (products.length === 0) {
    return null;
  }

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.title}>
            <span className={styles.titleBold}>{t('still_digging_around')}</span>
            <span className={styles.titleNormal}>{t('find_the_fi_that_fits')}</span>
          </h2>
          <div className={styles.headerActions}>
            <Link
              to={AppPaths.Compare}
              className={styles.yellowPill}
              onClick={() => events.crossShopSection.compareAllProductsTapped()}
            >
              {t('compare_all_products')}
            </Link>
            <button
              type="button"
              className={styles.chevronLink}
              onClick={() => {
                events.crossShopSection.takeTheQuizTapped();
                launchQuiz();
              }}
            >
              {t('take_the_quiz')}
              <Chevron />
            </button>
          </div>
        </div>
        <div className={products.length === 3 ? `${styles.grid} ${styles.gridThree}` : styles.grid}>
          {products.map((product) => (
            <CrossShopCard key={product.id} product={product} />
          ))}
        </div>
      </div>
    </section>
  );
}
